import {Driver} from "../Driver";
import {ConnectionIsNotSetError} from "../error/ConnectionIsNotSetError";
import {DriverOptions} from "../DriverOptions";
import {DatabaseConnection} from "../DatabaseConnection";
import {DriverUtils} from "../DriverUtils";
import {Logger} from "../../logger/Logger";
import {QueryRunner} from "../../query-runner/QueryRunner";
import {ColumnTypes} from "../../metadata/types/ColumnTypes";
import {ObjectLiteral} from "../../common/ObjectLiteral";
import {ColumnMetadata} from "../../metadata/ColumnMetadata";
import {DriverOptionNotSetError} from "../error/DriverOptionNotSetError";
import {DataTransformationUtils} from "../../util/DataTransformationUtils";
import {SqljsWorkerQueryRunner} from "./SqljsWorkerQueryRunner";
import {NamingStrategyInterface} from "../../naming-strategy/NamingStrategyInterface";
import {DriverPackageNotInstalledError} from "../error/DriverPackageNotInstalledError";
import * as fs from "fs";
import * as path from "path";
const Worker = require("workerjs");

/**
 * Organizes communication with sqljs in the browser.
 */
export class SqljsWorkerDriver implements Driver {

    // -------------------------------------------------------------------------
    // Public Properties
    // -------------------------------------------------------------------------

    /**
     * sql.js library.
     */
    // protected sqljs: any;

    /**
     * worker.sql.js library.
     */
    protected workerSqljs: any = undefined;
    /**
     * Naming strategy used in the connection where this driver is used.
     */
    namingStrategy: NamingStrategyInterface;

    /**
     * Driver connection options.
     */
    readonly options: DriverOptions;

    // -------------------------------------------------------------------------
    // Protected Properties
    // -------------------------------------------------------------------------

    /**
     * Connection to database.
     */
    protected databaseConnection: DatabaseConnection|undefined;

    /**
     * Logger used to log queries and errors.
     */
    protected logger: Logger;

    // -------------------------------------------------------------------------
    // Constructor
    // -------------------------------------------------------------------------

    constructor(options: DriverOptions, logger: Logger, workerSqljs?: any) {

        this.options = DriverUtils.buildDriverOptions(options);
        this.logger = logger;
        this.workerSqljs = workerSqljs;

        // validate options to make sure everything is set
        if (!this.options.storage)
            throw new DriverOptionNotSetError("storage");

        // if sqljs package instance was not set explicitly then try to load it
        if (!workerSqljs) {
            this.loadDependencies();
        } else {
            workerSqljs.terminate();
            this.loadDependencies();
        }
    }

    // -------------------------------------------------------------------------
    // Public Methods
    // -------------------------------------------------------------------------

    /**
     * Performs connection to the database.
     * Based on pooling options, it can either create connection immediately,
     * either create a pool and create connection when needed.
     */
    connect(): Promise<void> {

        // build connection options for the driver
        const options = Object.assign({}, {
            storage: this.options.storage
        }, this.options.extra || {});
        try {
            const buffer: Buffer = fs.readFileSync(options.storage);
            return this.setConnection(buffer);
        } catch (error) {
            return this.setConnection();
        }

    }

    private setConnection(buffer?: Buffer) {
        return new Promise<void>((ok, fail) => {

            let msg: any = {
                id: 1,
                action: "open"
            };
            if (buffer) {
                msg.buffer = this.toBuffer(buffer);
            }

            this.workerSqljs.postMessage(msg);

            this.workerSqljs.onerror = (e: any) => {
                fail(e);
            };

            this.workerSqljs.onmessage = (data: any, error: any) => {
                this.databaseConnection = {
                    id: 1,
                    connection: this.workerSqljs,
                    isTransactionActive: false
                };

                this.workerSqljs.onerror = (error: any) => {
                    fail(error);
                };

                this.workerSqljs.postMessage({
                    id: 1,
                    action: "each",
                    sql: `PRAGMA foreign_keys = ON;`
                });

                this.workerSqljs.onmessage = () => {
                    ok();
                };
            };


        });
    }

    /**
     * Closes connection with the database.
     */
    disconnect(): Promise<void> {
        return new Promise<void>((ok, fail) => {
            if (!this.databaseConnection)
                throw new ConnectionIsNotSetError("sqljsworker");
            try {
                this.workerSqljs.postMessage({
                    action: "close"
                });
                console.log("disconnect");
                this.workerSqljs.terminate();
                this.databaseConnection.connection.terminate();
                this.databaseConnection = undefined;
                return ok();
            } catch (error) {
                console.log(error);
            }
        });
    }

    commitStorage() {

        return new Promise<void>((ok, fail) => {

            if (!this.databaseConnection)
                throw new ConnectionIsNotSetError("sqljsworker");

            this.workerSqljs.postMessage({
                action: "export",
            });

            this.workerSqljs.onmessage = (event: any) => {

                if (!this.options.storage)
                    throw new DriverOptionNotSetError("storage");

                try {
                    const buffer = event.data.buffer;
                    fs.writeFileSync(this.options.storage, Buffer.from(this.toBuffer(buffer)));
                    ok();
                } catch (error) {
                    fail(error);
                }
            };
        });
    }

    toBuffer(buf: any) {
        const newBuffer = [];
        let i = 0;
        for (let p in buf) {
            newBuffer[i] = buf[p];
            i++;
        }
        return newBuffer;
    }

    /**
     * Creates a query runner used for common queries.
     */
    async createQueryRunner(): Promise<QueryRunner> {
        if (!this.databaseConnection)
            return Promise.reject(new ConnectionIsNotSetError("sqljsworker"));

        const databaseConnection = await this.retrieveDatabaseConnection();
        return new SqljsWorkerQueryRunner(databaseConnection, this, this.logger);
    }

    /**
     * Access to the native implementation of the database.
     */
    nativeInterface() {
        return {
            connection: this.databaseConnection ? this.databaseConnection.connection : undefined
        };
    }

    /**
     * Replaces parameters in the given sql with special escaping character
     * and an array of parameter names to be passed to a query.
     */
    escapeQueryWithParameters(sql: string, parameters: ObjectLiteral): [string, any[]] {
        if (!parameters || !Object.keys(parameters).length)
            return [sql, []];
        const escapedParameters: any[] = [];
        const keys = Object.keys(parameters).map(parameter => "(:" + parameter + "\\b)").join("|");
        sql = sql.replace(new RegExp(keys, "g"), (key: string) => {
            escapedParameters.push(parameters[key.substr(1)]);
            return "?";
        }); // todo: make replace only in value statements, otherwise problems
        return [sql, escapedParameters];
    }

    /**
     * Escapes a column name.
     */
    escapeColumnName(columnName: string): string {
        return columnName; // "`" + columnName + "`";
    }

    /**
     * Escapes an alias.
     */
    escapeAliasName(aliasName: string): string {
        return aliasName; // "`" + aliasName + "`";
    }

    /**
     * Escapes a table name.
     */
    escapeTableName(tableName: string): string {
        return tableName; // "`" + tableName + "`";
    }

    /**
     * Prepares given value to a value to be persisted, based on its column type and metadata.
     */
    preparePersistentValue(value: any, columnMetadata: ColumnMetadata): any {
        if (value === null || value === undefined)
            return null;

        switch (columnMetadata.type) {
            case ColumnTypes.BOOLEAN:
                return value === true ? 1 : 0;

            case ColumnTypes.DATE:
                return DataTransformationUtils.mixedDateToDateString(value);

            case ColumnTypes.TIME:
                return DataTransformationUtils.mixedDateToTimeString(value);

            case ColumnTypes.DATETIME:
                if (columnMetadata.localTimezone) {
                    return DataTransformationUtils.mixedDateToDatetimeString(value);
                } else {
                    return DataTransformationUtils.mixedDateToUtcDatetimeString(value);
                }

            case ColumnTypes.JSON:
                return JSON.stringify(value);

            case ColumnTypes.SIMPLE_ARRAY:
                return DataTransformationUtils.simpleArrayToString(value);
        }

        return value;
    }

    /**
     * Prepares given value to a value to be persisted, based on its column type or metadata.
     */
    prepareHydratedValue(value: any, columnMetadata: ColumnMetadata): any {
        switch (columnMetadata.type) {
            case ColumnTypes.BOOLEAN:
                return value ? true : false;

            case ColumnTypes.DATETIME:
                return DataTransformationUtils.normalizeHydratedDate(value, columnMetadata.localTimezone === true);

            case ColumnTypes.TIME:
                return DataTransformationUtils.mixedTimeToString(value);

            case ColumnTypes.JSON:
                return JSON.parse(value);

            case ColumnTypes.SIMPLE_ARRAY:
                return DataTransformationUtils.stringToSimpleArray(value);
        }

        return value;
    }

    // -------------------------------------------------------------------------
    // Protected Methods
    // -------------------------------------------------------------------------

    /**
     * Retrieves a new database connection.
     * If pooling is enabled then connection from the pool will be retrieved.
     * Otherwise active connection will be returned.
     */
    protected retrieveDatabaseConnection(): Promise<DatabaseConnection> {
        if (this.databaseConnection)
            return Promise.resolve(this.databaseConnection);

        throw new ConnectionIsNotSetError("sqljsworker");
    }

    /**
     * If driver dependency is not given explicitly, then try to load it via "require".
     */
    protected loadDependencies(): void {
        try {
            this.workerSqljs = new Worker(path.join(process.cwd() + "/worker.sql.js"), true);

        } catch (e) { // todo: better error for browser env
            throw new DriverPackageNotInstalledError("sqljsworker", "sql.js/js/worker.sql.js");
        }
    }
}